import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Package, Recycle, Inbox, FileText, Receipt, TrendingUp } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, Legend, CartesianGrid, AreaChart, Area } from "recharts";

const CARDS = [
  { key: "products", label: "Products", icon: Package, color: "text-blue-700 bg-blue-50" },
  { key: "refurbished", label: "Refurbished", icon: Recycle, color: "text-amber-700 bg-amber-50" },
  { key: "enquiries_new", label: "New Enquiries", icon: Inbox, color: "text-teal-700 bg-teal-50" },
  { key: "enquiries_total", label: "Total Enquiries", icon: TrendingUp, color: "text-slate-700 bg-slate-100" },
  { key: "quotations", label: "Quotations", icon: FileText, color: "text-indigo-700 bg-indigo-50" },
  { key: "invoices", label: "Invoices", icon: Receipt, color: "text-emerald-700 bg-emerald-50" },
];

const STATUS_COLORS = { new: "#0D9488", contacted: "#1D4ED8", quoted: "#6366F1", closed: "#64748B" };

const shortDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
  } catch { return iso; }
};

export default function AdminDashboard() {
  const [s, setS] = useState({});
  const [a, setA] = useState(null);
  const [range, setRange] = useState(30);

  useEffect(() => { api.get("/stats").then(r => setS(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get(`/analytics/enquiries?days=${range}`).then(r => setA(r.data)).catch(() => {}); }, [range]);

  const daily = (a?.daily || []).map(d => ({ ...d, label: shortDate(d.date) }));
  const byStatus = (a?.by_status || []).filter(s => s.count > 0);
  const byProduct = a?.by_product || [];

  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tight text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>Dashboard</h1>
      <p className="mt-2 text-sm text-slate-500">Overview of catalog, enquiries and billing.</p>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map(({ key, label, icon: Icon, color }) => (
          <div key={key} data-testid={`stat-${key}`} className="rounded-xl border border-slate-200 bg-white p-6">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${color}`}><Icon className="h-5 w-5" /></div>
            <div className="mt-4 text-3xl font-bold text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>{s[key] ?? 0}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-between">
        <h2 className="text-xl font-bold text-[#0F2942]" style={{ fontFamily: "Outfit, sans-serif" }}>Enquiry Analytics</h2>
        <div className="flex gap-1 rounded-full border border-slate-200 bg-white p-0.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setRange(d)}
              data-testid={`range-${d}`}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${range === d ? "bg-[#0F2942] text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >{d}d</button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="chart-daily">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Enquiries per Day</div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={daily} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradEnq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0D9488" stopOpacity={0.5}/>
                    <stop offset="95%" stopColor="#0D9488" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} interval={Math.max(0, Math.floor(daily.length / 8))} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Area type="monotone" dataKey="count" stroke="#0D9488" strokeWidth={2} fill="url(#gradEnq)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="chart-status">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status Breakdown</div>
          <div className="mt-4 h-64">
            {byStatus.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-slate-400">No data yet</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={byStatus} dataKey="count" nameKey="status" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {byStatus.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.status] || "#94A3B8"} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="chart-products">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Top Products by Enquiry ({range}d)</div>
        <div className="mt-4 h-64">
          {byProduct.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-slate-400">No data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byProduct} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11, fill: "#0F2942" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="count" fill="#1D4ED8" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
